// VIS-267 W7b: plain library materials that differ only by their values (colour, roughness, metalness) draw as ONE
// material, so the village's per-material batches (main.js bake) cost one draw call for all of them instead of one each.
// On real iPhones (Safari WebGPU) every draw costs ~0.1-0.2 ms of fixed time, whatever its size: Aurora's plinths, trims,
// roof edges, metal, posts and canvas were 6 draws, Lumo's 10.
//
// "Plain" = a MeshStandardNodeMaterial with no texture, no node slot, no hook (gpu/chain.js, gpu/lighting.js), no
// emissive (the night lights drive those), opaque, no userData. Everything else that the shader or the draw state reads
// must be equal (`valueKey`). The three values move into a small table (a uniform array, two rows per material); each
// vertex carries its row (`valueIndex`), read in the vertex stage and passed on as FLAT varyings, so every fragment gets
// exactly the float its own material had. What can move: the batch sorts at one depth (three sorts opaque draws by
// bounding-sphere centre, depth test less-or-equal), so where a merged part meets another material's at EXACTLY equal
// depth (an edge or seam both cover) a single MSAA sample may go to the other one (held frames, Aurora by day: 2-74
// isolated pixels over 8 levels per view on glass-kota cabin edges; Lumo none).
// Not merged across textures (no atlases: performance.md "Not done"), not in the editor's frame (Fine-tune's vertex runs
// are per library material: main.js keeps them there).
import * as THREE from 'three/webgpu';
import { attribute, int, uniformArray, varying } from 'three/tsl';

const TEXTURES = ['map', 'normalMap', 'emissiveMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'lightMap', 'alphaMap', 'bumpMap', 'displacementMap', 'envMap'];
const SLOTS = ['colorNode', 'normalNode', 'roughnessNode', 'metalnessNode', 'emissiveNode', 'positionNode', 'opacityNode', 'outputNode', 'fragmentNode', 'vertexNode', 'castShadowNode', 'castShadowPositionNode', 'receivedShadowNode', 'receivedShadowPositionNode', 'maskNode', 'maskShadowNode', 'aoNode', 'mrtNode', 'envNode', 'lightsNode', 'backdropNode', 'backdropAlphaNode', 'depthNode', 'shadowPositionNode'];
// what the shader or the draw state reads besides the three values: equal on every material of a group
const STATE = ['side', 'shadowSide', 'flatShading', 'envMapIntensity', 'envMapRotation', 'fog', 'lights', 'dithering', 'toneMapped', 'depthTest', 'depthWrite', 'depthFunc', 'colorWrite', 'stencilWrite', 'polygonOffset', 'polygonOffsetFactor', 'polygonOffsetUnits', 'blending', 'wireframe', 'visible', 'premultipliedAlpha', 'forceSinglePass', 'alphaHash', 'clipping', 'clipShadows', 'clipIntersection', 'normalScale', 'aoMapIntensity', 'lightMapIntensity'];

/** The part of a material that is not a value, as a string: equal keys draw the same picture with each other's
 *  colour, roughness and metalness. null: not a plain material (see the top of the file). */
export function valueKey(m) {
  if (!m || m.type !== 'MeshStandardNodeMaterial' || m.constructor !== THREE.MeshStandardNodeMaterial) return null;
  if (TEXTURES.some((k) => m[k]) || SLOTS.some((k) => m[k])) return null;
  if (m.emissive?.getHex() !== 0 || m.transparent || m.opacity !== 1 || m.alphaTest > 0 || m.alphaToCoverage || m.vertexColors) return null;
  if (Object.getOwnPropertySymbols(m).length || Object.getOwnPropertyNames(m).some((k) => typeof m[k] === 'function')) return null; // a hook of gpu/lighting.js or gpu/chain.js
  if (Object.keys(m.userData).length || m.clippingPlanes?.length) return null;
  return JSON.stringify([m.defines ?? null, ...STATE.map((k) => (m[k]?.toArray ? m[k].toArray() : m[k] ?? null))]);
}

/**
 * Groups `[[material, geometries], ...]` (one batch per material, in draw order) by `valueKey`: each group of two or
 * more materials becomes one shared material (one per group, built once) whose geometries carry `valueIndex`.
 * Returns { batches: [[material, geometries], ...] in the same order (a group sits where its first member was),
 *           shared: Map(source material -> shared material), regroup(batches) (the same for more batches) }.
 * The geometries of a shared group keep only what the material reads: position and normal (as plain floats, so
 * parts of different sources merge), the index, valueIndex, and the baked light's bakedUv where a part has it. A material with a part without normals keeps its own.
 * `more` (optional): more batches (the seasonal ones) whose plain materials join the same groups; `regroup(more)`
 * then gives them their shared materials.
 */
export function shareValues(batches, more = []) {
  const groups = new Map(), all = [...batches, ...more]; // groups: key -> [material, ...]
  const unfit = new Set(all.filter(([, list]) => !list.every((g) => g.attributes.position && g.attributes.normal)).map(([mat]) => mat));
  for (const [mat] of all) {
    const k = unfit.has(mat) ? null : valueKey(mat);
    if (k === null) continue;
    if (!groups.has(k)) groups.set(k, []);
    if (!groups.get(k).includes(mat)) groups.get(k).push(mat);
  }
  const shared = new Map();
  for (const mats of groups.values()) {
    if (mats.length < 2) continue;
    const m = sharedMaterial(mats);
    for (const src of mats) shared.set(src, m);
  }
  return { batches: regroup(batches, shared), shared, regroup: (b) => regroup(b, shared) };
}

/** [[material, geometries]] -> the same with every shared source's geometries under its shared material (first place). */
function regroup(batches, shared) {
  const out = [], at = new Map();
  for (const [mat, list] of batches) {
    const m = shared.get(mat);
    if (!m) { out.push([mat, list]); continue; }
    const row = ROWS.get(m).indexOf(mat);
    const geos = list.map((g) => plainGeometry(g, row));
    if (at.has(m)) at.get(m).push(...geos);
    else { const l = [...geos]; at.set(m, l); out.push([m, l]); }
  }
  return out;
}

/** One material for `mats` (equal valueKey): the first one's state, its values read from a table by valueIndex. */
export function sharedMaterial(mats) {
  const m = new THREE.MeshStandardNodeMaterial();
  m.copy(mats[0]);
  m.name = 'shared-values';
  m.color.set(0xffffff); m.roughness = 1; m.metalness = 0; // (unused: the nodes below carry the values)
  const rows = mats.flatMap((s) => [new THREE.Vector4(s.color.r, s.color.g, s.color.b, s.roughness), new THREE.Vector4(s.metalness, 0, 0, 0)]);
  const table = uniformArray(rows, 'vec4'), i = int(attribute('valueIndex', 'float')).mul(2);
  const a = varying(table.element(i)).setInterpolation('flat', 'either'), b = varying(table.element(i.add(1)).x).setInterpolation('flat', 'either');
  m.colorNode = a.xyz;
  m.roughnessNode = a.w;
  m.metalnessNode = b;
  m.userData.surfTable = true; // WP5.1a: the shared surface family's terms (gpu/surface.js: the baked light in a uniform If), so a batch with and one without baked parts are one program
  ROWS.set(m, mats);
  return m;
}
const ROWS = new WeakMap(); // shared material -> its source materials, in table order (row k = valueIndex k)
/** The source materials of a shared material, row by row (null for any other material). */
export const valueRows = (m) => ROWS.get(m) || null;

const floats = (a, n) => {
  if (a.array instanceof Float32Array && !a.normalized && !a.isInterleavedBufferAttribute) return a;
  const out = new Float32Array(a.count * n);
  for (let i = 0; i < a.count; i++) for (let c = 0; c < n; c++) out[i * n + c] = a.getComponent(i, c); // dequantised (normalised data read as its value)
  return new THREE.BufferAttribute(out, n);
};
/** A part of a shared batch: position, normal (plain floats), index (0..n-1 if it had none), valueIndex = its row. */
export function plainGeometry(g, row) {
  const c = new THREE.BufferGeometry(), n = g.attributes.position.count;
  c.setAttribute('position', floats(g.attributes.position, 3));
  c.setAttribute('normal', floats(g.attributes.normal, 3));
  c.setAttribute('valueIndex', new THREE.BufferAttribute(new Float32Array(n).fill(row), 1));
  if (g.attributes.bakedUv) c.setAttribute('bakedUv', g.attributes.bakedUv); // the baked light's coordinates (lazy/bakedlight.js: main.js gives the others of the batch zeros)
  c.setIndex(g.index || new THREE.BufferAttribute((n > 65535 ? Uint32Array : Uint16Array).from({ length: n }, (_, k) => k), 1));
  c.userData = g.userData;
  return c;
}
